import type { OperationsSnapshot, RunEvent, WebTestRun } from '@blume/web-test-model'

const hash = '9'.repeat(64)
const previousHash = '3'.repeat(64)
const createdAt = '2026-07-29T08:54:10.000Z'

export const fixtureOperations: OperationsSnapshot = {
  schemaVersion: 1,
  generatedAt: '2026-07-30T08:00:00.000Z',
  status: 'healthy',
  dependencies: [
    {
      id: 'verifier-api',
      label: 'Verifier API',
      status: 'healthy',
      detail:
        'Fixture: authenticated API is accepting new runs; dashboard traffic is capped at 3000/minute and session exchange at 10/IP/10 minutes'
    },
    {
      id: 'browser-run',
      label: 'Browser Run',
      status: 'healthy',
      detail: 'Fixture: browser binding configured'
    },
    {
      id: 'workflow',
      label: 'Durable Workflow',
      status: 'healthy',
      detail: 'Fixture: workflow binding configured'
    },
    {
      id: 'd1',
      label: 'D1 metadata',
      status: 'healthy',
      detail: 'Fixture: metadata query succeeded'
    },
    {
      id: 'r2',
      label: 'R2 evidence',
      status: 'healthy',
      detail: 'Fixture: private artifact binding configured'
    },
    {
      id: 'ai-gateway',
      label: 'Gemini gateway',
      status: 'healthy',
      detail: 'Fixture: verifier-owned model configured'
    }
  ],
  activity: { totalRuns: 148, runs24h: 18, activeRuns: 3, artifactBytes: 43_200_000 },
  limits: {
    activeRuns: 20,
    activeRunsPerRepository: 10,
    activeRunsPerPullRequest: 5,
    runsPerActorHour: 60,
    modelRequestsPerActorHour: 120,
    requestsPerActorMinute: 300,
    artifactBytes: 10_000_000,
    artifactBytesPerRun: 50_000_000
  },
  retention: {
    passingEvidenceDays: 7,
    passingReportDays: 14,
    nonPassingEvidenceDays: 30,
    scheduledCleanup: true
  },
  controls: {
    acceptingRuns: true,
    aiInferenceEnabled: true,
    ciRepository: 'blumepage/sidecar-prototype',
    previewHostSuffixes: ['.agentic-cms-olav.workers.dev'],
    dnsPublicAddressRequired: true,
    originIsolationEnabled: true
  }
}

export const fixtureRuns: WebTestRun[] = [
  {
    schemaVersion: 1,
    id: 'run-pr-482-navigation',
    suiteRunId: 'suite-pr-482',
    scenarioId: 'navigation.project-switch',
    scenarioHash: hash,
    planHash: previousHash,
    origin: 'github-pr',
    target: 'pr-web-lab',
    targetKind: 'web-lab',
    state: 'terminal',
    status: 'failed',
    classification: 'product-failure',
    source: {
      repository: 'blume-ai/blume',
      branch: 'feature/project-switcher',
      sha: '9f42cb7e21ab',
      pullRequest: 482,
      pullRequestMetadata: {
        title: 'Keep the selected project visible after switching',
        observedAt: '2026-07-29T08:55:02.000Z',
        description:
          'Updates the project switcher so the workspace heading immediately reflects the selected project. Includes navigation coverage for the changed state transition.',
        status: 'ready',
        author: {
          login: 'olavblj',
          avatarUrl: 'https://avatars.githubusercontent.com/u/10499451?v=4'
        },
        checks: { passed: 8, total: 9 },
        reviewThreads: { unresolved: 1, total: 4 }
      }
    },
    initiatedBy: 'github-actions',
    createdAt,
    startedAt: '2026-07-29T08:54:15.000Z',
    finishedAt: '2026-07-29T08:55:32.000Z',
    durationMs: 77_000,
    currentStageSummary: 'The project switcher does not reveal the selected project',
    reproductionCommand: 'pnpm web-tests run navigation.project-switch --target local-web-lab',
    diagnosis: {
      classification: 'product-failure',
      confidence: 0.94,
      summary:
        'Both clean attempts selected “Atlas”, but the workspace heading remained “Blume”. The locator is stable and the click was acknowledged, which points to a product state update failure.',
      evidenceArtifactIds: ['artifact-failure', 'artifact-trace'],
      evidenceStepIds: ['attempt-2-step-3'],
      nextAction: 'Inspect the project-selection state update introduced in PR #482.'
    },
    attempts: [
      {
        id: 'attempt-1',
        number: 1,
        purpose: 'deterministic',
        status: 'failed',
        startedAt: '2026-07-29T08:54:15.000Z',
        finishedAt: '2026-07-29T08:54:47.000Z',
        durationMs: 32_000,
        artifactIds: ['artifact-failure'],
        steps: [
          {
            id: 'attempt-1-step-1',
            scenarioStepId: 'open-project-switcher',
            description: 'Open the project switcher',
            status: 'passed',
            durationMs: 821,
            locatorSummary: 'button “Current project: Blume”',
            actionSummary: 'click',
            artifactIds: []
          },
          {
            id: 'attempt-1-step-2',
            scenarioStepId: 'select-atlas',
            description: 'Select Atlas',
            status: 'passed',
            durationMs: 534,
            locatorSummary: 'option “Atlas”',
            actionSummary: 'click',
            artifactIds: []
          },
          {
            id: 'attempt-1-step-3',
            scenarioStepId: 'confirm-project',
            description: 'The Atlas project becomes active',
            status: 'failed',
            durationMs: 5_132,
            locatorSummary: 'heading level 1',
            expected: 'Atlas',
            actual: 'Blume',
            error: 'Expected heading text to contain “Atlas”; received “Blume”.',
            artifactIds: ['artifact-failure']
          }
        ]
      },
      {
        id: 'attempt-2',
        number: 2,
        purpose: 'clean-retry',
        status: 'failed',
        startedAt: '2026-07-29T08:54:50.000Z',
        finishedAt: '2026-07-29T08:55:24.000Z',
        durationMs: 34_000,
        artifactIds: [
          'artifact-before',
          'artifact-baseline',
          'artifact-failure',
          'artifact-diff',
          'artifact-trace',
          'artifact-recording'
        ],
        steps: [
          {
            id: 'attempt-2-step-1',
            scenarioStepId: 'open-project-switcher',
            description: 'Open the project switcher',
            status: 'passed',
            durationMs: 786,
            locatorSummary: 'button “Current project: Blume”',
            actionSummary: 'click',
            artifactIds: ['artifact-before']
          },
          {
            id: 'attempt-2-step-2',
            scenarioStepId: 'select-atlas',
            description: 'Select Atlas',
            status: 'passed',
            durationMs: 492,
            locatorSummary: 'option “Atlas”',
            actionSummary: 'click',
            artifactIds: []
          },
          {
            id: 'attempt-2-step-3',
            scenarioStepId: 'confirm-project',
            description: 'The Atlas project becomes active',
            status: 'failed',
            durationMs: 5_087,
            locatorSummary: 'heading level 1',
            expected: 'Atlas',
            actual: 'Blume',
            error: 'Expected heading text to contain “Atlas”; received “Blume”.',
            artifactIds: ['artifact-baseline', 'artifact-failure', 'artifact-diff']
          }
        ]
      }
    ],
    artifacts: [
      {
        id: 'artifact-before',
        kind: 'screenshot-before',
        name: 'open-project-switcher-before.png',
        contentType: 'image/png',
        byteSize: 171_202,
        sha256: previousHash,
        redacted: false,
        createdAt: '2026-07-29T08:54:51.000Z',
        expiresAt: '2026-08-12T08:54:51.000Z'
      },
      {
        id: 'artifact-baseline',
        kind: 'screenshot-baseline',
        name: 'confirm-project-baseline.png',
        contentType: 'image/png',
        byteSize: 179_884,
        sha256: previousHash,
        redacted: false,
        createdAt: '2026-07-29T08:55:21.000Z',
        expiresAt: '2026-08-12T08:55:21.000Z'
      },
      {
        id: 'artifact-failure',
        kind: 'screenshot-failure',
        name: 'confirm-project-failure.png',
        contentType: 'image/png',
        byteSize: 182_440,
        sha256: hash,
        redacted: false,
        createdAt: '2026-07-29T08:55:22.000Z',
        expiresAt: '2026-08-12T08:55:22.000Z'
      },
      {
        id: 'artifact-diff',
        kind: 'screenshot-diff',
        name: 'confirm-project-diff.png',
        contentType: 'image/png',
        byteSize: 91_240,
        sha256: hash,
        redacted: false,
        createdAt: '2026-07-29T08:55:22.000Z',
        expiresAt: '2026-08-12T08:55:22.000Z'
      },
      {
        id: 'artifact-recording',
        kind: 'recording',
        name: 'attempt-2-recording.webm',
        contentType: 'video/webm',
        byteSize: 8_889,
        sha256: '10d619b20afb831bcf4bee2ae4a4636fd7fe7ec71a182feecb06595e870027d7',
        redacted: false,
        createdAt: '2026-07-29T08:55:24.000Z',
        expiresAt: '2026-08-12T08:55:24.000Z'
      },
      {
        id: 'artifact-trace',
        kind: 'trace',
        name: 'attempt-2-trace.zip',
        contentType: 'application/zip',
        byteSize: 1_822_901,
        sha256: previousHash,
        redacted: false,
        createdAt: '2026-07-29T08:55:24.000Z',
        expiresAt: '2026-08-12T08:55:24.000Z'
      },
      {
        id: 'artifact-console',
        kind: 'console-log',
        name: 'console.json',
        contentType: 'application/json',
        byteSize: 2_482,
        sha256: hash,
        redacted: true,
        createdAt: '2026-07-29T08:55:24.000Z',
        expiresAt: '2026-08-12T08:55:24.000Z'
      }
    ],
    modelUsage: [
      {
        model: 'google/gemini-3.6-flash',
        gatewayRequestId: 'req_01K1B8C35',
        inputTokens: 2_104,
        outputTokens: 286,
        estimatedCostUsd: 0,
        latencyMs: 1_482
      }
    ]
  },
  {
    schemaVersion: 1,
    id: 'run-pr-482-setup',
    suiteRunId: 'suite-pr-482',
    scenarioId: 'setup.first-run',
    scenarioHash: hash,
    planHash: previousHash,
    origin: 'github-pr',
    target: 'pr-web-lab',
    targetKind: 'web-lab',
    state: 'terminal',
    status: 'passed',
    source: {
      repository: 'blume-ai/blume',
      pullRequest: 482,
      sha: '9f42cb7e21ab',
      pullRequestMetadata: {
        title: 'Keep the selected project visible after switching',
        observedAt: '2026-07-29T08:55:02.000Z',
        description:
          'Updates the project switcher so the workspace heading immediately reflects the selected project. Includes navigation coverage for the changed state transition.',
        status: 'ready',
        author: {
          login: 'olavblj',
          avatarUrl: 'https://avatars.githubusercontent.com/u/10499451?v=4'
        },
        checks: { passed: 8, total: 9 },
        reviewThreads: { unresolved: 1, total: 4 }
      }
    },
    initiatedBy: 'github-actions',
    createdAt: '2026-07-29T08:53:50.000Z',
    startedAt: '2026-07-29T08:54:02.000Z',
    finishedAt: '2026-07-29T08:54:31.000Z',
    durationMs: 29_000,
    currentStageSummary: 'All 8 steps passed',
    attempts: [],
    artifacts: [],
    modelUsage: []
  },
  {
    schemaVersion: 1,
    id: 'run-pr-482-conversations',
    suiteRunId: 'suite-pr-482',
    scenarioId: 'conversations.filtering',
    scenarioHash: hash,
    planHash: previousHash,
    origin: 'github-pr',
    target: 'pr-web-lab',
    targetKind: 'web-lab',
    state: 'running',
    source: {
      repository: 'blume-ai/blume',
      pullRequest: 482,
      sha: '9f42cb7e21ab',
      pullRequestMetadata: {
        title: 'Keep the selected project visible after switching',
        observedAt: '2026-07-29T08:55:02.000Z',
        description:
          'Updates the project switcher so the workspace heading immediately reflects the selected project. Includes navigation coverage for the changed state transition.',
        status: 'ready',
        author: {
          login: 'olavblj',
          avatarUrl: 'https://avatars.githubusercontent.com/u/10499451?v=4'
        },
        checks: { passed: 8, total: 9 },
        reviewThreads: { unresolved: 1, total: 4 }
      }
    },
    initiatedBy: 'github-actions',
    createdAt: '2026-07-29T08:55:02.000Z',
    startedAt: '2026-07-29T08:55:06.000Z',
    currentStageSummary: 'Step 4 of 7 · Filter conversations by harness',
    attempts: [],
    artifacts: [],
    modelUsage: []
  },
  {
    schemaVersion: 1,
    id: 'run-local-electron',
    scenarioId: 'settings.theme',
    scenarioHash: hash,
    planHash: previousHash,
    origin: 'mcp',
    target: 'local-electron',
    targetKind: 'electron',
    state: 'terminal',
    status: 'passed',
    classification: 'flaky',
    source: { branch: 'feature/theme-cleanup', sha: '4cb7d91' },
    initiatedBy: 'claude-code',
    createdAt: '2026-07-29T08:37:02.000Z',
    startedAt: '2026-07-29T08:37:08.000Z',
    finishedAt: '2026-07-29T08:38:44.000Z',
    durationMs: 96_000,
    currentStageSummary: 'Passed on clean retry',
    attempts: [],
    artifacts: [],
    modelUsage: []
  },
  {
    schemaVersion: 1,
    id: 'run-needs-review',
    scenarioId: 'usage.weekly-summary',
    scenarioHash: hash,
    origin: 'cli',
    target: 'local-web-lab',
    targetKind: 'web-lab',
    state: 'terminal',
    status: 'needs-review',
    classification: 'test-drift',
    source: { branch: 'main', sha: '76ac201' },
    initiatedBy: 'olav',
    createdAt: '2026-07-29T07:14:02.000Z',
    startedAt: '2026-07-29T07:14:08.000Z',
    finishedAt: '2026-07-29T07:15:41.000Z',
    durationMs: 93_000,
    currentStageSummary: 'A candidate locator plan is ready for review',
    attempts: [],
    artifacts: [],
    modelUsage: []
  },
  {
    schemaVersion: 1,
    id: 'run-infrastructure-error',
    scenarioId: 'conversations.live-updates',
    scenarioHash: hash,
    origin: 'github-pr',
    target: 'pr-web-lab',
    targetKind: 'web-lab',
    state: 'terminal',
    status: 'infrastructure-error',
    classification: 'infrastructure-error',
    source: { repository: 'blume-ai/blume', pullRequest: 481, sha: '8ac45d2' },
    initiatedBy: 'github-actions',
    createdAt: '2026-07-29T06:14:02.000Z',
    startedAt: '2026-07-29T06:14:08.000Z',
    finishedAt: '2026-07-29T06:14:39.000Z',
    durationMs: 31_000,
    currentStageSummary: 'Browser Run capacity was unavailable; product assertions did not run',
    attempts: [],
    artifacts: [],
    modelUsage: []
  },
  {
    schemaVersion: 1,
    id: 'run-cancelled',
    scenarioId: 'setup.import-project',
    scenarioHash: hash,
    origin: 'mcp',
    target: 'local-web-lab',
    targetKind: 'web-lab',
    state: 'terminal',
    status: 'cancelled',
    source: { branch: 'feature/import-flow', sha: '1b2c3d4' },
    initiatedBy: 'codex',
    createdAt: '2026-07-29T05:44:02.000Z',
    startedAt: '2026-07-29T05:44:08.000Z',
    finishedAt: '2026-07-29T05:44:24.000Z',
    durationMs: 16_000,
    currentStageSummary: 'Cancelled when a newer local run started',
    attempts: [],
    artifacts: [],
    modelUsage: []
  },
  {
    schemaVersion: 1,
    id: 'run-scenario-drift',
    scenarioId: 'insights.empty-state-copy',
    scenarioHash: hash,
    planHash: previousHash,
    origin: 'github-pr',
    target: 'pr-web-lab',
    targetKind: 'web-lab',
    state: 'terminal',
    status: 'failed',
    classification: 'scenario-drift',
    source: { repository: 'blume-ai/blume', pullRequest: 479, sha: 'aa92f01' },
    initiatedBy: 'github-actions',
    createdAt: '2026-07-29T04:12:02.000Z',
    startedAt: '2026-07-29T04:12:08.000Z',
    finishedAt: '2026-07-29T04:12:57.000Z',
    durationMs: 49_000,
    currentStageSummary: 'The scenario still expects copy removed by the approved content change',
    attempts: [],
    artifacts: [],
    modelUsage: []
  },
  /*
   * The AI-authoring loop, live. These runs exercise the states the rest of
   * the fixture set never reaches: an agent exploring a brand-new scenario,
   * a candidate plan being stabilized, and a repaired plan awaiting review.
   */
  {
    schemaVersion: 1,
    id: 'run-mcp-exploring',
    scenarioId: 'navigation.pin-project',
    scenarioHash: hash,
    origin: 'mcp',
    target: 'local-web-lab',
    targetKind: 'web-lab',
    state: 'exploring',
    source: { branch: 'feature/project-pinning', sha: '7e91af3' },
    initiatedBy: 'claude-code',
    createdAt: '2026-07-29T09:02:12.000Z',
    startedAt: '2026-07-29T09:02:18.000Z',
    currentStageSummary: 'The agent is working out how to pin a project — attempt 1, step 3',
    attempts: [
      {
        id: 'explore-discovery-1',
        number: 1,
        purpose: 'discovery',
        startedAt: '2026-07-29T09:02:31.000Z',
        artifactIds: [],
        steps: [
          {
            id: 'explore-step-1',
            scenarioStepId: 'open-project-switcher',
            description: 'Open the project switcher',
            status: 'passed',
            startedAt: '2026-07-29T09:02:36.000Z',
            durationMs: 1_204,
            locatorSummary: 'button “Current project: Blume”',
            actionSummary: 'click',
            artifactIds: ['artifact-explore-switcher']
          },
          {
            id: 'explore-step-2',
            scenarioStepId: 'reveal-pin-control',
            description: 'Reveal the pin control on Atlas',
            status: 'passed',
            startedAt: '2026-07-29T09:03:02.000Z',
            durationMs: 2_310,
            locatorSummary: 'option “Atlas”',
            actionSummary: 'hover',
            artifactIds: []
          },
          {
            id: 'explore-step-3',
            scenarioStepId: 'pin-atlas',
            description: 'Pin the Atlas project',
            status: 'running',
            startedAt: '2026-07-29T09:03:12.000Z',
            locatorSummary: 'button “Pin Atlas”',
            actionSummary: 'hover, then click',
            artifactIds: []
          }
        ]
      }
    ],
    artifacts: [
      {
        id: 'artifact-explore-switcher',
        kind: 'screenshot-after',
        name: 'open-project-switcher.png',
        contentType: 'image/png',
        byteSize: 168_204,
        sha256: hash,
        redacted: false,
        createdAt: '2026-07-29T09:02:47.000Z',
        expiresAt: '2026-08-12T09:02:47.000Z'
      }
    ],
    modelUsage: [
      {
        model: 'google/gemini-3.6-flash',
        gatewayRequestId: 'req_01K1EXPLORE',
        inputTokens: 8_412,
        outputTokens: 1_022,
        estimatedCostUsd: 0,
        latencyMs: 2_218
      }
    ]
  },
  {
    schemaVersion: 1,
    id: 'run-mcp-stabilizing',
    scenarioId: 'navigation.default-project',
    scenarioHash: hash,
    origin: 'mcp',
    target: 'local-web-lab',
    targetKind: 'web-lab',
    state: 'stabilizing',
    source: { branch: 'feature/default-project', sha: 'c2d4e6f' },
    initiatedBy: 'codex',
    createdAt: '2026-07-29T09:01:12.000Z',
    startedAt: '2026-07-29T09:01:20.000Z',
    currentStageSummary:
      'Clean replay 2 of 2 — a second agreement commits the candidate for review',
    attempts: [
      {
        id: 'stab-discovery-1',
        number: 1,
        purpose: 'discovery',
        status: 'passed',
        startedAt: '2026-07-29T09:01:34.000Z',
        finishedAt: '2026-07-29T09:03:58.000Z',
        durationMs: 144_000,
        artifactIds: [],
        steps: [
          {
            id: 'stab-d-1',
            scenarioStepId: 'open-project-switcher',
            description: 'Open the project switcher',
            status: 'passed',
            startedAt: '2026-07-29T09:01:48.000Z',
            durationMs: 1_102,
            locatorSummary: 'button “Current project: Blume”',
            actionSummary: 'click',
            artifactIds: ['artifact-stab-switcher']
          },
          {
            id: 'stab-d-2',
            scenarioStepId: 'open-project-settings',
            description: 'Open the Atlas project settings',
            status: 'passed',
            startedAt: '2026-07-29T09:02:20.000Z',
            durationMs: 954,
            locatorSummary: 'menuitem “Project settings”',
            actionSummary: 'click',
            artifactIds: []
          },
          {
            id: 'stab-d-3',
            scenarioStepId: 'set-default-project',
            description: 'Make Atlas the default project',
            status: 'passed',
            startedAt: '2026-07-29T09:03:04.000Z',
            durationMs: 1_480,
            locatorSummary: 'checkbox “Make Atlas my default project”',
            actionSummary: 'check',
            artifactIds: []
          },
          {
            id: 'stab-d-4',
            scenarioStepId: 'confirm-default-project',
            description: 'The confirmation toast appears',
            status: 'passed',
            startedAt: '2026-07-29T09:03:41.000Z',
            durationMs: 2_216,
            locatorSummary: 'text “Atlas is now your default project”',
            artifactIds: []
          }
        ]
      },
      {
        id: 'stab-replay-1',
        number: 2,
        purpose: 'stabilization',
        status: 'passed',
        startedAt: '2026-07-29T09:05:02.000Z',
        finishedAt: '2026-07-29T09:05:41.000Z',
        durationMs: 39_000,
        artifactIds: [],
        steps: [
          {
            id: 'stab-r1-1',
            scenarioStepId: 'open-project-switcher',
            description: 'Open the project switcher',
            status: 'passed',
            durationMs: 1_040,
            artifactIds: []
          },
          {
            id: 'stab-r1-2',
            scenarioStepId: 'open-project-settings',
            description: 'Open the Atlas project settings',
            status: 'passed',
            durationMs: 902,
            artifactIds: []
          },
          {
            id: 'stab-r1-3',
            scenarioStepId: 'set-default-project',
            description: 'Make Atlas the default project',
            status: 'passed',
            durationMs: 1_318,
            artifactIds: []
          },
          {
            id: 'stab-r1-4',
            scenarioStepId: 'confirm-default-project',
            description: 'The confirmation toast appears',
            status: 'passed',
            durationMs: 1_984,
            artifactIds: []
          }
        ]
      },
      {
        id: 'stab-replay-2',
        number: 3,
        purpose: 'stabilization',
        startedAt: '2026-07-29T09:05:55.000Z',
        artifactIds: [],
        steps: [
          {
            id: 'stab-r2-1',
            scenarioStepId: 'open-project-switcher',
            description: 'Open the project switcher',
            status: 'passed',
            startedAt: '2026-07-29T09:05:58.000Z',
            durationMs: 1_012,
            artifactIds: []
          },
          {
            id: 'stab-r2-2',
            scenarioStepId: 'open-project-settings',
            description: 'Open the Atlas project settings',
            status: 'passed',
            startedAt: '2026-07-29T09:06:04.000Z',
            durationMs: 918,
            artifactIds: []
          },
          {
            id: 'stab-r2-3',
            scenarioStepId: 'set-default-project',
            description: 'Make Atlas the default project',
            status: 'running',
            startedAt: '2026-07-29T09:06:09.000Z',
            artifactIds: []
          }
        ]
      }
    ],
    artifacts: [
      {
        id: 'artifact-stab-switcher',
        kind: 'screenshot-after',
        name: 'open-project-switcher.png',
        contentType: 'image/png',
        byteSize: 166_882,
        sha256: hash,
        redacted: false,
        createdAt: '2026-07-29T09:01:52.000Z',
        expiresAt: '2026-08-12T09:01:52.000Z'
      },
      {
        id: 'artifact-candidate-plan',
        kind: 'candidate-plan',
        name: 'navigation.default-project.plan.json',
        contentType: 'application/json',
        byteSize: 6_248,
        sha256: previousHash,
        redacted: false,
        createdAt: '2026-07-29T09:04:41.000Z',
        expiresAt: '2026-08-28T09:04:41.000Z'
      }
    ],
    modelUsage: [
      {
        model: 'google/gemini-3.6-flash',
        gatewayRequestId: 'req_01K1STABLZ',
        inputTokens: 14_818,
        outputTokens: 2_106,
        estimatedCostUsd: 0,
        latencyMs: 3_412
      }
    ]
  },
  {
    schemaVersion: 1,
    id: 'run-repair-review',
    scenarioId: 'usage.export-csv',
    scenarioHash: hash,
    planHash: previousHash,
    origin: 'mcp',
    target: 'local-web-lab',
    targetKind: 'web-lab',
    state: 'terminal',
    status: 'needs-review',
    classification: 'test-drift',
    source: { branch: 'main', sha: 'e4f5a6b' },
    initiatedBy: 'claude-code',
    createdAt: '2026-07-29T09:20:04.000Z',
    startedAt: '2026-07-29T09:20:11.000Z',
    finishedAt: '2026-07-29T09:26:30.000Z',
    durationMs: 379_000,
    currentStageSummary: 'A repaired plan passed two validations and awaits human review',
    diagnosis: {
      classification: 'test-drift',
      confidence: 0.88,
      summary:
        'The committed plan clicked menu item “Export CSV”, which PR #478 renamed to “Download as CSV”. The product’s export flow still works — the test’s locator drifted.',
      evidenceArtifactIds: ['artifact-repair-diff'],
      evidenceStepIds: ['repair-det-step-3'],
      nextAction: 'Review the repaired plan; accepting it commits the new locator for future runs.'
    },
    attempts: [
      {
        id: 'repair-det-1',
        number: 1,
        purpose: 'deterministic',
        status: 'failed',
        startedAt: '2026-07-29T09:20:11.000Z',
        finishedAt: '2026-07-29T09:20:58.000Z',
        durationMs: 47_000,
        artifactIds: [],
        steps: [
          {
            id: 'repair-det-step-1',
            scenarioStepId: 'open-usage',
            description: 'Open the usage page',
            status: 'passed',
            durationMs: 1_820,
            actionSummary: 'navigate /usage',
            artifactIds: []
          },
          {
            id: 'repair-det-step-2',
            scenarioStepId: 'open-export-menu',
            description: 'Open the export menu',
            status: 'passed',
            durationMs: 866,
            locatorSummary: 'button “Export”',
            actionSummary: 'click',
            artifactIds: []
          },
          {
            id: 'repair-det-step-3',
            scenarioStepId: 'choose-csv',
            description: 'Choose CSV export',
            status: 'failed',
            durationMs: 8_042,
            locatorSummary: 'menuitem “Export CSV”',
            expected: 'menuitem “Export CSV” is visible',
            actual: 'No matching element — a menu item “Download as CSV” is present instead',
            error: 'No element matched role=menuitem “Export CSV” within 8s.',
            artifactIds: []
          }
        ]
      },
      {
        id: 'repair-disc-1',
        number: 2,
        purpose: 'repair-discovery',
        status: 'passed',
        startedAt: '2026-07-29T09:21:40.000Z',
        finishedAt: '2026-07-29T09:24:02.000Z',
        durationMs: 142_000,
        artifactIds: [],
        steps: []
      },
      {
        id: 'repair-val-1',
        number: 3,
        purpose: 'repair-validation',
        status: 'passed',
        startedAt: '2026-07-29T09:24:20.000Z',
        finishedAt: '2026-07-29T09:25:10.000Z',
        durationMs: 50_000,
        artifactIds: [],
        steps: []
      },
      {
        id: 'repair-val-2',
        number: 4,
        purpose: 'repair-validation',
        status: 'passed',
        startedAt: '2026-07-29T09:25:22.000Z',
        finishedAt: '2026-07-29T09:26:08.000Z',
        durationMs: 46_000,
        artifactIds: [],
        steps: []
      }
    ],
    artifacts: [
      {
        id: 'artifact-repair-candidate',
        kind: 'candidate-plan',
        name: 'usage.export-csv.plan.json',
        contentType: 'application/json',
        byteSize: 4_812,
        sha256: hash,
        redacted: false,
        createdAt: '2026-07-29T09:26:12.000Z',
        expiresAt: '2026-08-28T09:26:12.000Z'
      },
      {
        id: 'artifact-repair-diff',
        kind: 'plan-diff',
        name: 'usage.export-csv.plan-diff.json',
        contentType: 'application/json',
        byteSize: 1_644,
        sha256: previousHash,
        redacted: false,
        createdAt: '2026-07-29T09:26:14.000Z',
        expiresAt: '2026-08-28T09:26:14.000Z'
      }
    ],
    modelUsage: [
      {
        model: 'google/gemini-3.6-flash',
        gatewayRequestId: 'req_01K1REPAIR',
        inputTokens: 11_204,
        outputTokens: 1_648,
        estimatedCostUsd: 0,
        latencyMs: 2_866
      }
    ]
  }
]

export const fixtureEvents: RunEvent[] = [
  {
    schemaVersion: 1,
    id: 'run-pr-482-navigation:0',
    runId: 'run-pr-482-navigation',
    sequence: 0,
    occurredAt: '2026-07-29T08:54:10.000Z',
    type: 'run-state',
    summary: 'Queued for durable verification',
    state: 'queued'
  },
  {
    schemaVersion: 1,
    id: 'run-pr-482-navigation:1',
    runId: 'run-pr-482-navigation',
    sequence: 1,
    occurredAt: '2026-07-29T08:54:15.000Z',
    type: 'attempt-started',
    summary: 'Running committed deterministic plan',
    state: 'running',
    attemptId: 'attempt-1'
  },
  {
    schemaVersion: 1,
    id: 'run-pr-482-navigation:2',
    runId: 'run-pr-482-navigation',
    sequence: 2,
    occurredAt: '2026-07-29T08:54:50.000Z',
    type: 'attempt-started',
    summary: 'Clean retry started after equivalent failure',
    state: 'running',
    attemptId: 'attempt-2'
  },
  {
    schemaVersion: 1,
    id: 'run-pr-482-navigation:3',
    runId: 'run-pr-482-navigation',
    sequence: 3,
    occurredAt: '2026-07-29T08:55:25.000Z',
    type: 'run-state',
    summary: 'Classifying two equivalent failures',
    state: 'diagnosing'
  },
  {
    schemaVersion: 1,
    id: 'run-pr-482-navigation:4',
    runId: 'run-pr-482-navigation',
    sequence: 4,
    occurredAt: '2026-07-29T08:55:32.000Z',
    type: 'diagnosis-created',
    summary: 'Product failure · 94% confidence',
    state: 'terminal',
    status: 'failed',
    classification: 'product-failure'
  },
  /* run-pr-482-conversations — a deterministic replay still in flight. The
   * run has no durable execution yet; the live view renders from these
   * events alone. */
  {
    schemaVersion: 1,
    id: 'run-pr-482-conversations:0',
    runId: 'run-pr-482-conversations',
    sequence: 0,
    occurredAt: '2026-07-29T08:55:02.000Z',
    type: 'run-state',
    summary: 'Queued for durable verification',
    state: 'queued'
  },
  {
    schemaVersion: 1,
    id: 'run-pr-482-conversations:1',
    runId: 'run-pr-482-conversations',
    sequence: 1,
    occurredAt: '2026-07-29T08:55:06.000Z',
    type: 'run-state',
    summary: 'Provisioning the PR preview Web Lab',
    state: 'preparing'
  },
  {
    schemaVersion: 1,
    id: 'run-pr-482-conversations:2',
    runId: 'run-pr-482-conversations',
    sequence: 2,
    occurredAt: '2026-07-29T08:55:12.000Z',
    type: 'run-state',
    summary: 'Replaying the committed plan deterministically',
    state: 'running'
  },
  {
    schemaVersion: 1,
    id: 'run-pr-482-conversations:3',
    runId: 'run-pr-482-conversations',
    sequence: 3,
    occurredAt: '2026-07-29T08:55:12.000Z',
    type: 'attempt-started',
    summary: 'Deterministic replay of the committed plan',
    attemptId: 'conv-attempt-1'
  },
  {
    schemaVersion: 1,
    id: 'run-pr-482-conversations:4',
    runId: 'run-pr-482-conversations',
    sequence: 4,
    occurredAt: '2026-07-29T08:55:14.000Z',
    type: 'step-started',
    summary: 'Open the conversations list',
    attemptId: 'conv-attempt-1',
    stepId: 'conv-step-1'
  },
  {
    schemaVersion: 1,
    id: 'run-pr-482-conversations:5',
    runId: 'run-pr-482-conversations',
    sequence: 5,
    occurredAt: '2026-07-29T08:55:17.000Z',
    type: 'step-finished',
    summary: 'The conversations list rendered 24 sessions',
    attemptId: 'conv-attempt-1',
    stepId: 'conv-step-1',
    status: 'passed'
  },
  {
    schemaVersion: 1,
    id: 'run-pr-482-conversations:6',
    runId: 'run-pr-482-conversations',
    sequence: 6,
    occurredAt: '2026-07-29T08:55:19.000Z',
    type: 'step-started',
    summary: 'Open the harness filter menu',
    attemptId: 'conv-attempt-1',
    stepId: 'conv-step-2'
  },
  {
    schemaVersion: 1,
    id: 'run-pr-482-conversations:7',
    runId: 'run-pr-482-conversations',
    sequence: 7,
    occurredAt: '2026-07-29T08:55:21.000Z',
    type: 'step-finished',
    summary: 'The filter menu lists Claude, Codex, and Gemini',
    attemptId: 'conv-attempt-1',
    stepId: 'conv-step-2',
    status: 'passed'
  },
  {
    schemaVersion: 1,
    id: 'run-pr-482-conversations:8',
    runId: 'run-pr-482-conversations',
    sequence: 8,
    occurredAt: '2026-07-29T08:55:22.000Z',
    type: 'step-started',
    summary: 'Choose the Claude harness',
    attemptId: 'conv-attempt-1',
    stepId: 'conv-step-3'
  },
  {
    schemaVersion: 1,
    id: 'run-pr-482-conversations:9',
    runId: 'run-pr-482-conversations',
    sequence: 9,
    occurredAt: '2026-07-29T08:55:24.000Z',
    type: 'step-finished',
    summary: 'Filter applied — the URL carries harness=claude',
    attemptId: 'conv-attempt-1',
    stepId: 'conv-step-3',
    status: 'passed'
  },
  {
    schemaVersion: 1,
    id: 'run-pr-482-conversations:10',
    runId: 'run-pr-482-conversations',
    sequence: 10,
    occurredAt: '2026-07-29T08:55:26.000Z',
    type: 'step-started',
    summary: 'Filter conversations by harness',
    attemptId: 'conv-attempt-1',
    stepId: 'conv-step-4'
  },
  {
    schemaVersion: 1,
    id: 'run-pr-482-conversations:11',
    runId: 'run-pr-482-conversations',
    sequence: 11,
    occurredAt: '2026-07-29T08:55:41.000Z',
    type: 'heartbeat',
    summary: 'Still working — the filtered list is loading',
    attemptId: 'conv-attempt-1'
  },
  /* run-mcp-exploring — the agent authoring a brand-new test, live. */
  {
    schemaVersion: 1,
    id: 'run-mcp-exploring:0',
    runId: 'run-mcp-exploring',
    sequence: 0,
    occurredAt: '2026-07-29T09:02:12.000Z',
    type: 'run-state',
    summary: 'Queued by claude-code over MCP',
    state: 'queued'
  },
  {
    schemaVersion: 1,
    id: 'run-mcp-exploring:1',
    runId: 'run-mcp-exploring',
    sequence: 1,
    occurredAt: '2026-07-29T09:02:18.000Z',
    type: 'run-state',
    summary: 'Provisioning a browser against the developer’s local Web Lab',
    state: 'preparing'
  },
  {
    schemaVersion: 1,
    id: 'run-mcp-exploring:2',
    runId: 'run-mcp-exploring',
    sequence: 2,
    occurredAt: '2026-07-29T09:02:31.000Z',
    type: 'run-state',
    summary: 'No committed plan exists — the agent will work the test out live',
    state: 'exploring'
  },
  {
    schemaVersion: 1,
    id: 'run-mcp-exploring:3',
    runId: 'run-mcp-exploring',
    sequence: 3,
    occurredAt: '2026-07-29T09:02:31.000Z',
    type: 'attempt-started',
    summary: 'Discovery attempt — the agent narrates as it drives the app',
    attemptId: 'explore-discovery-1'
  },
  {
    schemaVersion: 1,
    id: 'run-mcp-exploring:4',
    runId: 'run-mcp-exploring',
    sequence: 4,
    occurredAt: '2026-07-29T09:02:34.000Z',
    type: 'log',
    summary: 'Goal: pin Atlas so it stays at the top of the project switcher',
    attemptId: 'explore-discovery-1'
  },
  {
    schemaVersion: 1,
    id: 'run-mcp-exploring:5',
    runId: 'run-mcp-exploring',
    sequence: 5,
    occurredAt: '2026-07-29T09:02:47.000Z',
    type: 'log',
    summary: 'Observed: the switcher lists Blume, Atlas, Sandbox — a pin control appears on hover',
    attemptId: 'explore-discovery-1',
    stepId: 'explore-step-1'
  },
  {
    schemaVersion: 1,
    id: 'run-mcp-exploring:6',
    runId: 'run-mcp-exploring',
    sequence: 6,
    occurredAt: '2026-07-29T09:02:47.000Z',
    type: 'artifact-created',
    summary: 'Captured the open project switcher',
    attemptId: 'explore-discovery-1',
    stepId: 'explore-step-1',
    artifactId: 'artifact-explore-switcher'
  },
  {
    schemaVersion: 1,
    id: 'run-mcp-exploring:7',
    runId: 'run-mcp-exploring',
    sequence: 7,
    occurredAt: '2026-07-29T09:03:05.000Z',
    type: 'log',
    summary: 'Trying: hover the Atlas row to reveal its pin affordance',
    attemptId: 'explore-discovery-1',
    stepId: 'explore-step-2'
  },
  {
    schemaVersion: 1,
    id: 'run-mcp-exploring:8',
    runId: 'run-mcp-exploring',
    sequence: 8,
    occurredAt: '2026-07-29T09:03:12.000Z',
    type: 'log',
    summary: 'Trying: click the pin control — first try missed; it is only visible while hovered',
    attemptId: 'explore-discovery-1',
    stepId: 'explore-step-3'
  },
  {
    schemaVersion: 1,
    id: 'run-mcp-exploring:9',
    runId: 'run-mcp-exploring',
    sequence: 9,
    occurredAt: '2026-07-29T09:03:20.000Z',
    type: 'log',
    summary: 'Retrying with a hover-then-click sequence and the accessible name “Pin Atlas”',
    attemptId: 'explore-discovery-1',
    stepId: 'explore-step-3'
  },
  /* run-mcp-stabilizing — discovery done, plan compiled, clean replays running. */
  {
    schemaVersion: 1,
    id: 'run-mcp-stabilizing:0',
    runId: 'run-mcp-stabilizing',
    sequence: 0,
    occurredAt: '2026-07-29T09:01:12.000Z',
    type: 'run-state',
    summary: 'Queued by codex over MCP',
    state: 'queued'
  },
  {
    schemaVersion: 1,
    id: 'run-mcp-stabilizing:1',
    runId: 'run-mcp-stabilizing',
    sequence: 1,
    occurredAt: '2026-07-29T09:01:20.000Z',
    type: 'run-state',
    summary: 'Provisioning a browser against the developer’s local Web Lab',
    state: 'preparing'
  },
  {
    schemaVersion: 1,
    id: 'run-mcp-stabilizing:2',
    runId: 'run-mcp-stabilizing',
    sequence: 2,
    occurredAt: '2026-07-29T09:01:34.000Z',
    type: 'run-state',
    summary: 'No committed plan exists — the agent will work the test out live',
    state: 'exploring'
  },
  {
    schemaVersion: 1,
    id: 'run-mcp-stabilizing:3',
    runId: 'run-mcp-stabilizing',
    sequence: 3,
    occurredAt: '2026-07-29T09:01:34.000Z',
    type: 'attempt-started',
    summary: 'Discovery attempt — the agent narrates as it drives the app',
    attemptId: 'stab-discovery-1'
  },
  {
    schemaVersion: 1,
    id: 'run-mcp-stabilizing:4',
    runId: 'run-mcp-stabilizing',
    sequence: 4,
    occurredAt: '2026-07-29T09:03:58.000Z',
    type: 'attempt-finished',
    summary: 'Discovery succeeded — every scenario expectation was met once',
    attemptId: 'stab-discovery-1',
    status: 'passed'
  },
  {
    schemaVersion: 1,
    id: 'run-mcp-stabilizing:5',
    runId: 'run-mcp-stabilizing',
    sequence: 5,
    occurredAt: '2026-07-29T09:04:05.000Z',
    type: 'run-state',
    summary: 'Compiling the successful trace into a declarative plan with stable locators',
    state: 'compiling'
  },
  {
    schemaVersion: 1,
    id: 'run-mcp-stabilizing:6',
    runId: 'run-mcp-stabilizing',
    sequence: 6,
    occurredAt: '2026-07-29T09:04:22.000Z',
    type: 'log',
    summary: 'Locators chosen accessibility-first: 4 by role, 1 label fallback, no CSS'
  },
  {
    schemaVersion: 1,
    id: 'run-mcp-stabilizing:7',
    runId: 'run-mcp-stabilizing',
    sequence: 7,
    occurredAt: '2026-07-29T09:04:41.000Z',
    type: 'artifact-created',
    summary: 'Candidate plan drafted — 4 steps, accessibility-first locators',
    artifactId: 'artifact-candidate-plan'
  },
  {
    schemaVersion: 1,
    id: 'run-mcp-stabilizing:8',
    runId: 'run-mcp-stabilizing',
    sequence: 8,
    occurredAt: '2026-07-29T09:04:52.000Z',
    type: 'run-state',
    summary: 'Replaying the candidate plan cleanly to prove it is trustworthy',
    state: 'stabilizing'
  },
  {
    schemaVersion: 1,
    id: 'run-mcp-stabilizing:9',
    runId: 'run-mcp-stabilizing',
    sequence: 9,
    occurredAt: '2026-07-29T09:05:02.000Z',
    type: 'attempt-started',
    summary: 'Clean replay 1 of 2 — isolated profile, no AI in the loop',
    attemptId: 'stab-replay-1'
  },
  {
    schemaVersion: 1,
    id: 'run-mcp-stabilizing:10',
    runId: 'run-mcp-stabilizing',
    sequence: 10,
    occurredAt: '2026-07-29T09:05:41.000Z',
    type: 'attempt-finished',
    summary: 'Replay 1 agreed with the discovery trace on every step',
    attemptId: 'stab-replay-1',
    status: 'passed'
  },
  {
    schemaVersion: 1,
    id: 'run-mcp-stabilizing:11',
    runId: 'run-mcp-stabilizing',
    sequence: 11,
    occurredAt: '2026-07-29T09:05:55.000Z',
    type: 'attempt-started',
    summary: 'Clean replay 2 of 2 — a second agreement commits the candidate for review',
    attemptId: 'stab-replay-2'
  },
  /* run-repair-review — the repair loop that produced a reviewable candidate. */
  {
    schemaVersion: 1,
    id: 'run-repair-review:0',
    runId: 'run-repair-review',
    sequence: 0,
    occurredAt: '2026-07-29T09:20:04.000Z',
    type: 'run-state',
    summary: 'Queued by claude-code over MCP',
    state: 'queued'
  },
  {
    schemaVersion: 1,
    id: 'run-repair-review:1',
    runId: 'run-repair-review',
    sequence: 1,
    occurredAt: '2026-07-29T09:20:11.000Z',
    type: 'run-state',
    summary: 'Replaying the committed plan deterministically',
    state: 'running'
  },
  {
    schemaVersion: 1,
    id: 'run-repair-review:2',
    runId: 'run-repair-review',
    sequence: 2,
    occurredAt: '2026-07-29T09:20:11.000Z',
    type: 'attempt-started',
    summary: 'Deterministic replay of the committed plan',
    attemptId: 'repair-det-1'
  },
  {
    schemaVersion: 1,
    id: 'run-repair-review:3',
    runId: 'run-repair-review',
    sequence: 3,
    occurredAt: '2026-07-29T09:21:02.000Z',
    type: 'run-state',
    summary: 'The replay failed — deciding whose fault it is',
    state: 'diagnosing'
  },
  {
    schemaVersion: 1,
    id: 'run-repair-review:4',
    runId: 'run-repair-review',
    sequence: 4,
    occurredAt: '2026-07-29T09:21:40.000Z',
    type: 'run-state',
    summary: 'Diagnosis suspects locator drift — exploring a repair',
    state: 'exploring'
  },
  {
    schemaVersion: 1,
    id: 'run-repair-review:5',
    runId: 'run-repair-review',
    sequence: 5,
    occurredAt: '2026-07-29T09:21:40.000Z',
    type: 'attempt-started',
    summary: 'Repair discovery — the agent looks for the drifted control',
    attemptId: 'repair-disc-1'
  },
  {
    schemaVersion: 1,
    id: 'run-repair-review:6',
    runId: 'run-repair-review',
    sequence: 6,
    occurredAt: '2026-07-29T09:22:31.000Z',
    type: 'log',
    summary:
      'Found “Download as CSV” where “Export CSV” used to be — same handler, renamed by PR #478',
    attemptId: 'repair-disc-1'
  },
  {
    schemaVersion: 1,
    id: 'run-repair-review:7',
    runId: 'run-repair-review',
    sequence: 7,
    occurredAt: '2026-07-29T09:24:10.000Z',
    type: 'run-state',
    summary: 'Validating the repaired plan with clean replays',
    state: 'stabilizing'
  },
  {
    schemaVersion: 1,
    id: 'run-repair-review:8',
    runId: 'run-repair-review',
    sequence: 8,
    occurredAt: '2026-07-29T09:24:20.000Z',
    type: 'attempt-started',
    summary: 'Repair validation 1 of 2',
    attemptId: 'repair-val-1'
  },
  {
    schemaVersion: 1,
    id: 'run-repair-review:9',
    runId: 'run-repair-review',
    sequence: 9,
    occurredAt: '2026-07-29T09:25:22.000Z',
    type: 'attempt-started',
    summary: 'Repair validation 2 of 2',
    attemptId: 'repair-val-2'
  },
  {
    schemaVersion: 1,
    id: 'run-repair-review:10',
    runId: 'run-repair-review',
    sequence: 10,
    occurredAt: '2026-07-29T09:26:12.000Z',
    type: 'artifact-created',
    summary: 'Repaired candidate plan ready',
    artifactId: 'artifact-repair-candidate'
  },
  {
    schemaVersion: 1,
    id: 'run-repair-review:11',
    runId: 'run-repair-review',
    sequence: 11,
    occurredAt: '2026-07-29T09:26:14.000Z',
    type: 'artifact-created',
    summary: 'Plan diff against the committed plan',
    artifactId: 'artifact-repair-diff'
  },
  {
    schemaVersion: 1,
    id: 'run-repair-review:12',
    runId: 'run-repair-review',
    sequence: 12,
    occurredAt: '2026-07-29T09:26:30.000Z',
    type: 'diagnosis-created',
    summary: 'Test drift · 88% confidence — a repaired plan awaits review',
    state: 'terminal',
    status: 'needs-review',
    classification: 'test-drift'
  }
]
